import { useEffect, useState } from 'react';
import {
  Activity,
  Boxes,
  Building2,
  ChevronRight,
  CircleAlert,
  Database,
  HardDriveDownload,
  Users
} from 'lucide-react';
import { useNavigate } from '../../app/router';
import { readToken, superadminTokenKey } from '../../app/session';
import { ApiError, apiRequest } from '../../lib/api';

interface Overview {
  summary: {
    totalCompanies: number;
    activeCompanies: number;
    failedCompanies: number;
    employeeCapacity: number;
    enabledModules: number;
    unhealthyDatabases: number;
    successfulBackups: number;
  };
  issues: Array<{
    companyId: string;
    companyName: string;
    type: string;
    message: string;
  }>;
  activity: Array<{
    id: number;
    action: string;
    companyName: string | null;
    actorName: string | null;
    createdAt: string;
  }>;
}

const actionLabels: Record<string, string> = {
  'company.provisioned': 'Otvorena nova firma',
  'company.updated': 'Promijenjeni podaci firme',
  'company.modules_updated': 'Promijenjen paket modula',
  'company.status_updated': 'Promijenjen status firme',
  'backup.created': 'Izrađen backup baze',
  'platform.settings_updated': 'Promijenjene postavke sustava'
};

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 11) return 'Dobro jutro';
  if (hour < 18) return 'Dobar dan';
  return 'Dobra večer';
}

export function OverviewPage() {
  const navigate = useNavigate();
  const token = readToken(superadminTokenKey);
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiRequest<Overview>('/api/v1/superadmin/overview', { accessToken: token })
      .then(setData)
      .catch((caught) =>
        setError(caught instanceof ApiError ? caught.message : 'Pregled nije dostupan.')
      );
  }, [token]);

  return (
    <>
      <header className="page-header">
        <div>
          <span className="eyebrow">Upravljačko središte</span>
          <h1>{greeting()}, Luka</h1>
          <p>Sve firme, licence i tehničko zdravlje na jednom mjestu.</p>
        </div>
        <button
          className="button button--primary"
          onClick={() => navigate('/superadmin/companies?new=1')}
        >
          <Building2 size={18} /> Otvori novu firmu
        </button>
      </header>

      {error && <div className="form-error panel-error">{error}</div>}
      <section className="admin-kpis">
        <article><span>Aktivne firme</span><strong>{data?.summary.activeCompanies ?? '—'}</strong><Building2 /></article>
        <article><span>Kapacitet korisnika</span><strong>{data?.summary.employeeCapacity ?? '—'}</strong><Users /></article>
        <article><span>Uključeni moduli</span><strong>{data?.summary.enabledModules ?? '—'}</strong><Boxes /></article>
        <article><span>Upozorenja</span><strong>{(data?.summary.failedCompanies ?? 0) + (data?.summary.unhealthyDatabases ?? 0)}</strong><CircleAlert /></article>
      </section>

      <section className="overview-grid">
        <article className="panel">
          <div className="panel__toolbar">
            <div><h2>Treba tvoju pažnju</h2><p className="muted">Problemi koje nije dobro ostaviti za kasnije.</p></div>
            <Activity size={20} className="toolbar-icon" />
          </div>
          <div className="attention-list">
            {data?.issues.map((issue) => (
              <button
                key={`${issue.companyId}-${issue.type}`}
                onClick={() =>
                  navigate(`/superadmin/companies?company=${issue.companyId}`)
                }
              >
                <span className="attention-icon">
                  {issue.type === 'BACKUP_MISSING' ? <HardDriveDownload /> : <Database />}
                </span>
                <span><strong>{issue.companyName}</strong><small>{issue.message}</small></span>
                <ChevronRight />
              </button>
            ))}
            {data && !data.issues.length && (
              <div className="table-empty compact-empty">Sve je mirno i uredno.</div>
            )}
          </div>
        </article>

        <article className="panel">
          <div className="panel__toolbar">
            <div><h2>Nedavne aktivnosti</h2><p className="muted">Tko je i što promijenio.</p></div>
          </div>
          <div className="audit-list">
            {data?.activity.map((entry) => (
              <div key={entry.id}>
                <i />
                <span>
                  <strong>{actionLabels[entry.action] ?? entry.action}</strong>
                  <small>{entry.companyName ?? 'Platforma'} · {new Intl.DateTimeFormat('hr-HR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(entry.createdAt))}</small>
                </span>
              </div>
            ))}
            {data && !data.activity.length && (
              <div className="table-empty compact-empty">Još nema zabilježenih aktivnosti.</div>
            )}
          </div>
        </article>
      </section>
    </>
  );
}
